import { shallowEqual } from "react-redux";

export const checkTypeExhausted = (check: never): never => {
  return undefined as never;
};

//https://stackoverflow.com/questions/1960473/get-all-unique-values-in-a-javascript-array-remove-duplicates
export function onlyUnique<T>(value: T, index: number, array: T[]) {
  return array.indexOf(value) === index;
}

export function unique<T>(array: T[], equalityFn: (x1: T, x2: T) => boolean) {
  return array.reduce<T[]>(
    (unique, x) =>
      unique.find((y) => equalityFn(x, y)) ? unique : unique.concat(x),
    []
  );
}

export const asPercentageString = (num: number, decimalPlaces: number) => {
  if (isNaN(num)) {
    return "-";
  }

  return `${(num * 100).toFixed(decimalPlaces)}%`;
};

export function nonUndefined<T>(
  array: (T | undefined)[],
  alwaysFilter: boolean = false
) {
  if (!alwaysFilter && array.every((item) => item !== undefined)) {
    return array as T[];
  }

  return array.filter((item) => item !== undefined) as T[];
}

export function nonEmptyStrings(array: (string | undefined)[]): string[] {
  return array.filter((s) => !!s && s !== "") as string[];
}

export function pushX<T>(value: T, x: number, array: T[]) {
  for (var i = 0; i < x; i++) {
    array.push(value);
  }
}

export function unshiftX<T>(value: T, x: number, array: T[]) {
  for (var i = 0; i < x; i++) {
    array.unshift(value);
  }
}

export function positionNumberToText(pos: number) {
  const lastDigits = pos % 100;
  if (lastDigits === 11 || lastDigits === 12 || lastDigits === 13) {
    return `${pos}th`;
  }

  const lastDigit = pos % 10;
  switch (lastDigit) {
    case 1:
      return `${pos}st`;
    case 2:
      return `${pos}nd`;
    case 3:
      return `${pos}rd`;
    default:
      return `${pos}th`;
  }
}

export function numberToLetter(num: number) {
  var output = "";
  while (num > 26) {
    output += "A";
    num -= 26;
  }

  const unicodeNum = 64 + num;
  const finalLetter = String.fromCharCode(unicodeNum);
  output += finalLetter;
  return output;
}

export function numberToStringWithSign(
  num: number,
  zeroPositive: boolean = true
) {
  const isPositive = zeroPositive ? num >= 0 : num > 0;
  return `${isPositive ? "+" : ""}${num}`;
}

export function countToArray(count: number) {
  const numbers: number[] = [];
  for (var i = 1; i <= count; i++) {
    numbers.push(i);
  }

  return numbers;
}

export function matchingArrays<T>(a: T[], b: T[]) {
  if (a.length !== b.length) {
    return false;
  }

  for (var i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) {
      return false;
    }
  }

  return true;
}

export function arrayCompareWithShallowEquals<T>(a: T[], b: T[]) {
  if (a.length !== b.length) {
    return false;
  }

  for (let i = 0; i < a.length; i++) {
    if (!shallowEqual(a[i], b[i])) {
      return false;
    }
  }

  return true;
}

/**
 * Check if the search string is contained in any of the searching strings.
 * Case sensitive.
 * @param search The string to search for.
 * @param searching The strings to search in.
 * @returns True if the search string is contained in any of the searching strings.
 */
export function anyStringContainsCaseSensitive(
  search: string,
  ...searching: (string | undefined)[]
) {
  return searching.some((s) => s?.includes(search));
}

/**
 * Check if the search string is contained in any of the searching strings.
 * Case insensitive.
 * @param search The string to search for.
 * @param searching The strings to search in.
 * @returns True if the search string is contained in any of the searching strings.
 */
export function anyStringContains(
  search: string,
  ...searching: (string | undefined)[]
) {
  return searching.some((s) => s?.toLowerCase().includes(search.toLowerCase()));
}

export type SortOrder = "ASC" | "DESC";
const DEFAULT_SORT_ORDER = "ASC";
export function compareAlphabetically(
  a: string | undefined,
  b: string | undefined,
  order: SortOrder = DEFAULT_SORT_ORDER
) {
  if (a === b) {
    return 0;
  }

  if (!a) {
    return 1;
  }

  if (!b) {
    return -1;
  }

  return order === "ASC" ? a.localeCompare(b) : b.localeCompare(a);
}

export function timeAscending(a: number | undefined, b: number | undefined) {
  return compareTimes(a, b, "ASC");
}

export function compareTimes(
  a: number | undefined,
  b: number | undefined,
  order: SortOrder = "ASC"
) {
  if (a === b) {
    return 0;
  }

  // Sort undefined values to the bottom always
  if (a === undefined) {
    return 1;
  }

  // Sort undefined values to the bottom always
  if (b === undefined) {
    return -1;
  }

  // Sort DNFs (<0 values) as greater than set times
  if (a < 0) {
    return order === "ASC" ? 1 : -1;
  }

  // Sort DNFs (<0 values) as greater than set times
  if (b < 0) {
    return order === "ASC" ? -1 : 1;
  }

  return order === "ASC" ? a - b : b - a;
}

export function comparePoints(
  a: number | "Qual" | undefined,
  b: number | "Qual" | undefined,
  order: SortOrder = "ASC"
) {
  if (a === b) {
    return 0;
  }

  // Sort undefined values to the bottom always
  if (a === undefined) {
    return 1;
  }

  // Sort undefined values to the bottom always
  if (b === undefined) {
    return -1;
  }

  // Sort Qual as greater than number points
  if (a === "Qual") {
    return order === "ASC" ? 1 : -1;
  }

  // Sort Qual as greater than number points
  if (b === "Qual") {
    return order === "ASC" ? -1 : 1;
  }

  return order === "ASC" ? a - b : b - a;
}

export function normalize(
  value: number,
  lowerBound: number,
  upperBound: number
) {
  return (value - lowerBound) / (upperBound - lowerBound);
}

export function normalizeToRange(
  value: number,
  lowerBound: number,
  upperBound: number,
  minValue: number,
  maxValue: number
) {
  return (
    normalize(value, lowerBound, upperBound) * (maxValue - minValue) + minValue
  );
}
